import { toCanvas } from "html-to-image";
import jsPDF from "jspdf";
import { Employee } from "../../types/processTypes";
import { Company } from "../../../setups/types/setupTypes";

const PDF_IMAGE_OPTS = {
  format: "PNG" as const,
  x: 4,
  y: 4,
  width: 200,
  height: 290,
  compression: "FAST" as const,
};

function queryInRoot(root: HTMLElement | Document, id: string): HTMLElement | null {
  return root.querySelector(`#${id}`) as HTMLElement | null;
}

/** Matches PremiumTemplate download naming: `EXP CV Acme (Jane Doe)`. */
export function buildBulkCvFileName(employee: Employee, company: Company): string {
  const exp =
    (employee.workExperience?.length ?? 0) > 0 ? "EXP" : "NON EXP";
  const displayName = company.displayName ?? "";
  const space = displayName.indexOf(" ");
  const compName =
    space >= 0 ? displayName.substring(0, space) : displayName;
  return `${exp} CV ${compName} (${employee.givenName} ${employee.surName})`;
}

async function waitForImages(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              img.addEventListener("load", () => resolve(), { once: true });
              img.addEventListener("error", () => resolve(), { once: true });
            }),
    ),
  );
}

/**
 * html-to-image clones the DOM into an SVG foreignObject where percentage
 * widths (e.g. faceWidth "100%") often resolve against the wrong containing
 * block, stretching photos outside their grid cell. Pin mui-image wrappers
 * to their on-screen pixel size before capture.
 */
function pinMuiImageSizes(root: HTMLElement): () => void {
  const wrappers = root.querySelectorAll<HTMLElement>(".mui-image-wrapper");
  const restores: Array<() => void> = [];

  wrappers.forEach((wrapper) => {
    const rect = wrapper.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;

    const prevWrapper = {
      width: wrapper.style.width,
      height: wrapper.style.height,
      maxWidth: wrapper.style.maxWidth,
      overflow: wrapper.style.overflow,
    };
    wrapper.style.width = `${rect.width}px`;
    wrapper.style.height = `${rect.height}px`;
    wrapper.style.maxWidth = `${rect.width}px`;
    wrapper.style.overflow = "hidden";
    restores.push(() => {
      wrapper.style.width = prevWrapper.width;
      wrapper.style.height = prevWrapper.height;
      wrapper.style.maxWidth = prevWrapper.maxWidth;
      wrapper.style.overflow = prevWrapper.overflow;
    });

    const img = wrapper.querySelector("img");
    if (!img) return;

    const prevImg = {
      width: img.style.width,
      height: img.style.height,
      maxWidth: img.style.maxWidth,
      top: img.style.top,
      left: img.style.left,
      bottom: img.style.bottom,
      right: img.style.right,
    };
    img.style.width = "100%";
    img.style.height = "100%";
    img.style.maxWidth = "100%";
    // Clear mui-image shift offsets so the photo stays inside the box.
    img.style.top = "0";
    img.style.left = "0";
    img.style.bottom = "";
    img.style.right = "";
    restores.push(() => {
      img.style.width = prevImg.width;
      img.style.height = prevImg.height;
      img.style.maxWidth = prevImg.maxWidth;
      img.style.top = prevImg.top;
      img.style.left = prevImg.left;
      img.style.bottom = prevImg.bottom;
      img.style.right = prevImg.right;
    });
  });

  return () => {
    for (let i = restores.length - 1; i >= 0; i--) restores[i]();
  };
}

async function capturePage(el: HTMLElement): Promise<HTMLCanvasElement> {
  await waitForImages(el);
  const restore = pinMuiImageSizes(el);
  try {
    return await toCanvas(el, { cacheBust: true, pixelRatio: 2 });
  } finally {
    restore();
  }
}

export async function buildAndSavePdfFromDom(
  fileName: string,
  numberOfPages: number,
  root?: HTMLElement | null,
): Promise<void> {
  const scope: HTMLElement | Document = root ?? document;
  const personalEl = queryInRoot(scope, "personalData");
  if (!personalEl) return;

  const doc = new jsPDF("p", "mm", "a4", false);
  const canvas1 = await capturePage(personalEl);
  doc.addImage({
    imageData: canvas1.toDataURL("image/png"),
    ...PDF_IMAGE_OPTS,
    alias: "alias1",
  });

  if (numberOfPages >= 2) {
    const passportEl = queryInRoot(scope, "passportData");
    if (passportEl) {
      doc.addPage();
      const canvas2 = await capturePage(passportEl);
      doc.addImage({
        imageData: canvas2.toDataURL("image/png"),
        ...PDF_IMAGE_OPTS,
        alias: "alias2",
      });
    }
  }

  if (numberOfPages >= 3) {
    const medicalEl = queryInRoot(scope, "medicalData");
    if (medicalEl) {
      doc.addPage();
      const canvas3 = await capturePage(medicalEl);
      doc.addImage({
        imageData: canvas3.toDataURL("image/png"),
        ...PDF_IMAGE_OPTS,
        alias: "alias3",
      });
    }
  }

  doc.save(`${fileName}.pdf`);
}
